import * as Y from "yjs"
import {
  Awareness,
  applyAwarenessUpdate,
  encodeAwarenessUpdate,
  removeAwarenessStates,
} from "y-protocols/awareness"

import { recordClientCrdtDelta } from "@/lib/crdt/client-history-buffer"
import { isTrivialYjsUpdate } from "@/lib/crdt/crdt-history"
import type { AwarenessUserState } from "@/lib/crdt/awareness-user"
import type { LocalCrdtOrigin, RemoteCrdtOrigin } from "@/lib/compiler/types"
import { compilePdfTsLog } from "@/lib/compiler/compile-pdf-ts-log"
import { redactWsUrl } from "@/lib/compiler/same-origin-ws"
import {
  bytesPreview,
  encodeAwarenessMessage,
  encodeSyncStep1FromDoc,
  encodeSyncUpdate,
  extractRelayedYjsUpdate,
  isExactLengthPrefixedBlob,
  readSyncMessageFromFrame,
  remapBareSyncStepToFramed,
  syncSubtypeLabel,
  Y_MESSAGE_AWARENESS,
  Y_MESSAGE_AUTH,
  Y_MESSAGE_QUERY_AWARENESS,
  Y_MESSAGE_SYNC,
  Y_MESSAGE_SYNC_ALT,
  Y_SYNC_UPDATE,
} from "@/lib/crdt/y-sync-protocol"

const CRDT_DEBUG = process.env.NODE_ENV === "development"
const MAX_RECONNECT_ATTEMPTS = 12
const MAX_BACKOFF_MS = 10_000

function crdtWsAudit(...args: unknown[]) {
  console.log("[CRDT:WS]", ...args)
}

function crdtWsLog(level: "log" | "warn" | "error", ...args: unknown[]) {
  if (!CRDT_DEBUG) return
  console[level]("[CRDT WS]", ...args)
}

function isLocalCrdtOrigin(origin: unknown): origin is LocalCrdtOrigin {
  if (!origin || typeof origin !== "object") return false
  return (origin as Partial<LocalCrdtOrigin>).kind === "local"
}

function isRemoteCrdtOrigin(origin: unknown): origin is RemoteCrdtOrigin {
  if (!origin || typeof origin !== "object") return false
  return (origin as Partial<RemoteCrdtOrigin>).kind === "remote"
}

function isSeedCrdtOrigin(origin: unknown): boolean {
  if (!origin || typeof origin !== "object") return false
  return (origin as { kind?: unknown }).kind === "seed"
}

/** y-monaco uses the MonacoBinding instance as transaction origin; it exposes `.ytext`.
 *  We avoid relying on constructor.name because bundling/minification can rename it. */
function getMonacoBindingYText(origin: unknown): Y.Text | null {
  if (!origin || typeof origin !== "object") return null
  const ytext = (origin as { ytext?: unknown }).ytext
  return ytext instanceof Y.Text ? ytext : null
}

function isPlainIndexPosition(raw: unknown): boolean {
  if (!raw || typeof raw !== "object") return false
  const pos = raw as Record<string, unknown>
  return typeof pos.index === "number" && !("item" in pos) && !("tname" in pos)
}

function rehydrateAwarenessRelativePositions(state: Record<string, unknown>): void {
  const selectionKey = state.selection ? "selection" : null
  if (!selectionKey) return
  const selection = state[selectionKey]
  if (!selection || typeof selection !== "object") return

  for (const field of ["anchor", "head"] as const) {
    const raw = (selection as Record<string, unknown>)[field]
    if (!raw || typeof raw !== "object" || isPlainIndexPosition(raw)) continue
    const pos = raw as Record<string, unknown>
    // After JSON round-trip on the wire, Yjs relative positions are plain objects.
    if ("item" in pos || "tname" in pos) {
      try {
        ;(selection as Record<string, unknown>)[field] = Y.createRelativePositionFromJSON(
          pos as Parameters<typeof Y.createRelativePositionFromJSON>[0],
        )
      } catch {
        /* keep raw */
      }
    }
  }
}

export type CrdtWsConnectionStatus = "disconnected" | "connecting" | "connected"

export type CrdtWsSyncHandlers = {
  onStatus?: (status: CrdtWsConnectionStatus) => void
  /** Fired once per connect after first successful sync_step2 or sync_update. */
  onSynced?: () => void
  onError?: (event: Event) => void
  /**
   * MonacoBinding was editing a Y.Text that is no longer contentMap[path]
   * (dual-seed / map conflict). Caller should destroy+rebind Monaco to the map entry.
   */
  onOrphanYText?: (path: string) => void
}

export type CrdtWsConnectOptions = {
  /** Skip work when session generation changed (stale reconnect). */
  isStale?: () => boolean
  /** After reconnect, push full doc state as sync_update (demo resync path). */
  sendFullStateOnOpen?: boolean
  /** Relay awareness updates over the WebSocket (authenticated sessions). */
  awarenessWireEnabled?: boolean
  /** Re-resolve an authenticated WS URL before each reconnect (fresh JWT). */
  resolveWsUrl?: () => Promise<string>
  /** Optional WebSocket constructor (dev instrumentation). */
  WebSocketClass?: typeof WebSocket
}

/**
 * Manual CRDT WebSocket sync — demo-compatible protocol (sync + optional awareness on wire).
 *
 * Outgoing: local Y.Doc update → [0x00][0x02] + raw Yjs bytes → socket.send
 * Incoming: decodeYMessage → apply Y.applyUpdate(remote) → observers → React/Monaco
 */
export class CrdtWsSyncManager {
  private socket: WebSocket | null = null
  private doc: Y.Doc | null = null
  private docUpdateHandler: ((update: Uint8Array, origin: unknown) => void) | null = null
  private handlers: CrdtWsSyncHandlers = {}
  private wsUrl = ""
  private connectOptions: CrdtWsConnectOptions = {}
  private reconnectAttempt = 0
  private reconnectTimer: number | null = null
  private disposed = false
  private needsResync = false
  private _synced = false
  private _status: CrdtWsConnectionStatus = "disconnected"
  private sessionId: string | null = null
  private afterTransactionHandler: ((tx: Y.Transaction) => void) | null = null
  private inferredPathByOrigin = new WeakMap<object, string>()
  private lastInferredPath: string | undefined
  private awarenessWireEnabled = false
  private awarenessUpdateHandler:
    | ((change: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => void)
    | null = null
  private awarenessChangeLogHandler: (() => void) | null = null
  private peerJoinSyncHandler:
    | ((change: { added: number[]; updated: number[]; removed: number[] }, origin: unknown) => void)
    | null = null
  private lastDocSyncRecvAt = 0
  private lastDocSyncSendAt = 0
  /** Peer clientIDs we already pushed a one-time full doc snapshot to (map convergence). */
  private fullStatePushedToPeers = new Set<number>()
  /** Awareness for MonacoBinding cursors; relayed when awarenessWireEnabled. */
  readonly awareness: Awareness

  constructor(doc: Y.Doc) {
    this.awareness = new Awareness(doc)
  }

  get synced(): boolean {
    return this._synced
  }

  get status(): CrdtWsConnectionStatus {
    return this._status
  }

  /** Epoch-ms clocks for Compile:PDF:TS diagnostics (backend timestamp debugging). */
  getDocSyncTimestamps(): {
    lastDocSyncSendAt: number | null
    lastDocSyncRecvAt: number | null
    msSinceLastSend: number | null
    msSinceLastRecv: number | null
  } {
    const now = Date.now()
    return {
      lastDocSyncSendAt: this.lastDocSyncSendAt || null,
      lastDocSyncRecvAt: this.lastDocSyncRecvAt || null,
      msSinceLastSend: this.lastDocSyncSendAt ? now - this.lastDocSyncSendAt : null,
      msSinceLastRecv: this.lastDocSyncRecvAt ? now - this.lastDocSyncRecvAt : null,
    }
  }

  get url(): string {
    return this.wsUrl
  }

  setActiveFilePath(path: string | undefined): void {
    this.lastInferredPath = path
  }

  attachDoc(doc: Y.Doc): void {
    this.detachDoc()
    this.doc = doc
    this.docUpdateHandler = (update: Uint8Array, origin: unknown) => {
      // Remote updates (from backend) and seed ops must never be echoed back.
      if (isRemoteCrdtOrigin(origin) || isSeedCrdtOrigin(origin)) return
      if (isTrivialYjsUpdate(update)) return

      let fileID: string | undefined
      if (isLocalCrdtOrigin(origin)) {
        // Explicit local origin (updateYFile / createFileAtPath) — use provided fileID if available.
        const id = (origin as Partial<{ path?: string; fileID?: string }>).fileID ?? origin.path
        if (!id) {
          console.warn("[CRDT:Doc] SEND skipped — local origin missing fileID")
          return
        }
        fileID = id
      } else {
        const bindingYText = getMonacoBindingYText(origin)
        if (bindingYText) {
          // MonacoBinding updates carry the Y.Text instance. Resolve its actual path
          // in the files map rather than trusting the last active tab — updates can
          // be delayed and arrive after the user has switched files.
          const contentMap = doc.getMap<Y.Text>("content")
          contentMap.forEach((value, key) => {
            if (value === bindingYText) fileID = key
          })
          if (!fileID) {
            // The binding is editing a Y.Text no longer in contentMap (rename/delete
            // left an orphan). Do NOT merge into the active tab — that corrupts it.
            console.error("[CRDT:Doc] ORPHAN Y.Text — MonacoBinding Y.Text not in contentMap; discarding update", {
              orphanLen: bindingYText.toString().length,
              lastInferredPath: this.lastInferredPath,
              sessionId: this.sessionId,
            })
            this.handlers.onOrphanYText?.(this.lastInferredPath ?? "(unknown)")
            return
          }
        } else {
          console.warn("[CRDT:Doc] SEND skipped — cannot infer path for update", {
            origin: typeof origin,
            sessionId: this.sessionId,
          })
          return
        }
      }

      const originLabel = isLocalCrdtOrigin(origin)
        ? "local"
        : ((origin as { constructor?: { name?: string } })?.constructor?.name ?? typeof origin)
      console.log("[CRDT:Doc] SEND sync_update", {
        fileID: fileID ?? "(none)",
        bytes: update.byteLength,
        origin: originLabel,
        socketOpen: this.socket?.readyState === WebSocket.OPEN,
        sessionId: this.sessionId,
      })
      const sent = this.sendDocumentUpdate(update, fileID)
      if (!sent) {
        console.warn("[CRDT:Doc] SEND failed — socket not open or trivial; will need resync", {
          readyState: this.socket?.readyState,
          fileID: fileID ?? "(none)",
        })
      } else {
        this.lastDocSyncSendAt = Date.now()
      }
    }
    doc.on("update", this.docUpdateHandler)
  }

  detachDoc(): void {
    const doc = this.doc
    const handler = this.docUpdateHandler
    if (doc && handler) doc.off("update", handler)
    this.doc = null
    this.docUpdateHandler = null
    this.afterTransactionHandler = null
    this.lastInferredPath = undefined
    this.inferredPathByOrigin = new WeakMap<object, string>()
  }

  setHandlers(handlers: CrdtWsSyncHandlers): void {
    this.handlers = handlers
  }

  setSessionId(sessionId: string | null): void {
    this.sessionId = sessionId
  }

  setLocalAwarenessUser(user: AwarenessUserState): void {
    this.awareness.setLocalStateField("user", {
      name: user.name,
      color: user.color,
      colorLight: user.colorLight,
      initials: user.initials,
      username: user.username,
      avatarUrl: user.avatarUrl,
    })
  }

  setLocalAwarenessActiveFile(path: string | undefined): void {
    if (!path) {
      this.awareness.setLocalStateField("activeFile", null)
      return
    }
    this.awareness.setLocalStateField("activeFile", path)
  }

  /**
   * Publish the latest PDF generation so peers can refresh even when compiler WS
   * does not fan out pdf_ready. Uses the same awareness channel as cursors.
   */
  setLocalAwarenessPdfReady(
    info: { t: number; sessionId: string; compiledFile?: string | null } | null,
  ): void {
    if (!info) {
      this.awareness.setLocalStateField("pdfReady", null)
      return
    }
    // Awareness values are JSON-encoded on the wire — keep only plain JSON primitives
    // (no null nested fields) to avoid intermittent decode failures across browsers.
    const payload: Record<string, string | number> = {
      t: info.t,
      sessionId: info.sessionId,
      at: Date.now(),
    }
    if (info.compiledFile) payload.compiledFile = info.compiledFile
    this.awareness.setLocalStateField("pdfReady", payload)
  }

  /** Re-broadcast local awareness state (e.g. after profile load or socket open). */
  broadcastLocalAwareness(): void {
    if (!this.awarenessWireEnabled) return
    const socket = this.socket
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.log("[CRDT:Awareness] broadcast skipped — socket not open", {
        readyState: socket?.readyState,
      })
      return
    }
    const localState = this.awareness.getLocalState()
    if (!localState) {
      console.log("[CRDT:Awareness] broadcast skipped — no local state")
      return
    }
    console.log("[CRDT:Awareness] broadcasting local state", {
      user: (localState as { user?: { name?: string } }).user?.name,
      activeFile: (localState as { activeFile?: string }).activeFile,
      hasCursor: Boolean((localState as { cursor?: unknown }).cursor),
      peerCount: this.awareness.getStates().size - 1,
    })
    this.awareness.setLocalState(localState)
  }

  /** Force a reconnect with a fresh URL (tab visibility / stale token recovery). */
  reconnect(): void {
    if (this.disposed) return
    this.clearReconnectTimer()
    this.reconnectAttempt = 0
    void this.openSocketWithResolvedUrl()
  }

  /** Pull missing document updates from the server (e.g. after a backgrounded tab). */
  requestSyncFromServer(): boolean {
    const socket = this.socket
    const doc = this.doc
    if (!socket || socket.readyState !== WebSocket.OPEN || !doc) return false
    try {
      const framed = encodeSyncStep1FromDoc(doc)
      socket.send(new Uint8Array(framed))
      console.log("[CRDT:Doc] requestSyncFromServer (sync_step1)", {
        framedBytes: framed.byteLength,
        sessionId: this.sessionId,
      })
      return true
    } catch (error) {
      console.error("[CRDT:Doc] requestSyncFromServer failed", error)
      return false
    }
  }

  connect(wsUrl: string, options: CrdtWsConnectOptions = {}): void {
    this.wsUrl = wsUrl
    this.connectOptions = options
    this.awarenessWireEnabled = Boolean(options.awarenessWireEnabled)
    this.disposed = false
    this.clearReconnectTimer()
    if (options.resolveWsUrl) {
      void this.openSocketWithResolvedUrl()
    } else {
      this.openSocket()
    }
  }

  disconnect(): void {
    this.broadcastLocalAwarenessRemoval()
    this.disposed = true
    this.clearReconnectTimer()
    this.closeSocket()
    this.setStatus("disconnected")
    this._synced = false
  }

  destroy(): void {
    this.broadcastLocalAwarenessRemoval()
    this.detachAwarenessWire()
    this.disconnect()
    this.detachDoc()
    this.awareness.destroy()
  }

  private detachAwarenessWire(): void {
    if (this.awarenessUpdateHandler) {
      this.awareness.off("update", this.awarenessUpdateHandler)
      this.awarenessUpdateHandler = null
    }
    if (this.awarenessChangeLogHandler) {
      this.awareness.off("change", this.awarenessChangeLogHandler)
      this.awarenessChangeLogHandler = null
    }
    if (this.peerJoinSyncHandler) {
      this.awareness.off("change", this.peerJoinSyncHandler)
      this.peerJoinSyncHandler = null
    }
  }

  private attachAwarenessWire(): void {
    this.detachAwarenessWire()
    if (!this.awarenessWireEnabled) return

    this.awarenessUpdateHandler = (
      { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
      origin: unknown,
    ) => {
      if (origin === "remote") return
      const socket = this.socket
      if (!socket || socket.readyState !== WebSocket.OPEN) return

      const changedClients = added.concat(updated).concat(removed)
      if (changedClients.length === 0) return

      const encodedUpdate = encodeAwarenessUpdate(this.awareness, changedClients)
      if (encodedUpdate.byteLength === 0) return

      socket.send(new Uint8Array(encodeAwarenessMessage(encodedUpdate)))
      console.log("[CRDT:Awareness] update sent", {
        clients: changedClients.length,
        added: added.length,
        updated: updated.length,
        removed: removed.length,
      })
      crdtWsLog("log", "awareness update sent", { clients: changedClients.length })
    }
    this.awareness.on("update", this.awarenessUpdateHandler)

    // When a collaborator joins, re-run sync handshake and push a one-time full
    // snapshot. Without this, independently seeded Y.Map("files") entries never
    // converge — each tab edits its own Y.Text and peers see contentChanged:false.
    this.peerJoinSyncHandler = ({ added }, origin) => {
      if (origin !== "remote") return
      const remoteAdded = added.filter((id) => id !== this.doc?.clientID)
      if (remoteAdded.length === 0) return
      console.log("[CRDT:Doc] peer joined — requesting document sync + one-time full-state push", {
        added: remoteAdded,
        sessionId: this.sessionId,
      })
      this.requestSyncFromServer()
      if (this.doc && this.socket?.readyState === WebSocket.OPEN) {
        const newPeers = remoteAdded.filter((id) => !this.fullStatePushedToPeers.has(id))
        if (newPeers.length === 0) return
        try {
          const fullUpdate = Y.encodeStateAsUpdate(this.doc)
          if (!isTrivialYjsUpdate(fullUpdate)) {
            console.log("[CRDT:Doc] SEND one-time full-state sync_update on peer join", {
              bytes: fullUpdate.byteLength,
              peers: newPeers,
              sessionId: this.sessionId,
            })
            this.sendDocumentUpdate(fullUpdate, this.lastInferredPath)
            for (const id of newPeers) this.fullStatePushedToPeers.add(id)
          }
        } catch (error) {
          console.error("[CRDT:Doc] full-state push on peer join failed", error)
        }
      }
    }
    this.awareness.on("change", this.peerJoinSyncHandler)

    if (!this.awarenessChangeLogHandler) {
      this.awarenessChangeLogHandler = () => {
        const peers = this.awareness.getStates().size - 1
        console.log("[CRDT:Awareness] peers in room", {
          peerCount: peers,
          sessionId: this.sessionId,
          lastDocSyncSendAt: this.lastDocSyncSendAt || null,
          lastDocSyncRecvAt: this.lastDocSyncRecvAt || null,
          clients: Array.from(this.awareness.getStates().entries()).map(([id, s]) => ({
            id,
            self: id === this.doc?.clientID,
            name: (s as { user?: { name?: string } }).user?.name,
            activeFile: (s as { activeFile?: string }).activeFile,
            hasSelection: Boolean((s as { selection?: unknown }).selection),
            hasCursor: Boolean((s as { cursor?: unknown }).cursor),
          })),
        })
      }
      this.awareness.on("change", this.awarenessChangeLogHandler)
    }
  }

  private broadcastLocalAwarenessRemoval(): void {
    if (!this.awarenessWireEnabled) return
    const socket = this.socket
    if (!socket || socket.readyState !== WebSocket.OPEN) return
    try {
      removeAwarenessStates(this.awareness, [this.awareness.clientID], this)
    } catch {
      /* socket may already be closing */
    }
  }

  private handleAwarenessDisconnectCleanup(): void {
    if (!this.awarenessWireEnabled) return
    const remoteClients = Array.from(this.awareness.getStates().keys()).filter(
      (clientId) => clientId !== this.doc?.clientID,
    )
    if (remoteClients.length === 0) return
    removeAwarenessStates(this.awareness, remoteClients, "disconnect")
  }

  private setStatus(status: CrdtWsConnectionStatus): void {
    if (this._status === status) return
    this._status = status
    this.handlers.onStatus?.(status)
  }

  private markSynced(): void {
    const wasSynced = this._synced
    this._synced = true
    this.needsResync = false
    if (!wasSynced) this.handlers.onSynced?.()
  }

  /** Local edit → [0x00][0x02] + raw Yjs update (no length prefix). */
  sendDocumentUpdate(update: Uint8Array, fileID?: string): boolean {
    if (isTrivialYjsUpdate(update)) return false
    const socket = this.socket
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      this.needsResync = true
      console.warn("[CRDT:Doc] SEND skipped — socket not open", { fileID: fileID ?? "" })
      return false
    }
    const framed = encodeSyncUpdate(update)
    const sendAt = Date.now()
    console.log("[CRDT:Doc] SEND sync_update framed", {
      fileID: fileID ?? "(none)",
      rawBytes: update.byteLength,
      framedBytes: framed.byteLength,
      framedFirst10: bytesPreview(framed, 10),
      sessionId: this.sessionId,
    })
    compilePdfTsLog("crdt:send", "CRDT sync_update sent (local edit → server)", {
      sessionId: this.sessionId,
      fileID: fileID ?? null,
      rawBytes: update.byteLength,
      framedBytes: framed.byteLength,
      lastDocSyncRecvAt: this.lastDocSyncRecvAt || null,
      msSinceLastRecv: this.lastDocSyncRecvAt ? sendAt - this.lastDocSyncRecvAt : null,
    })
    socket.send(new Uint8Array(framed))
    const trimmedFileID = fileID?.trim()
    if (this.sessionId && trimmedFileID && update.byteLength <= 16_384) {
      recordClientCrdtDelta(this.sessionId, trimmedFileID, update)
    }
    this.lastDocSyncSendAt = sendAt
    return true
  }

  private async openSocketWithResolvedUrl(): Promise<void> {
    if (this.disposed) return
    if (this.connectOptions.resolveWsUrl) {
      try {
        const resolved = await this.connectOptions.resolveWsUrl()
        if (resolved) this.wsUrl = resolved
        console.log("[CRDT:WS] URL resolved for connect/reconnect")
      } catch (err) {
        console.warn("[CRDT:WS] resolveWsUrl failed, using cached url", err)
      }
    }
    if (this.disposed || this.connectOptions.isStale?.()) return
    this.openSocket()
  }

  private openSocket(): void {
    if (this.disposed || !this.wsUrl) return

    this.closeSocket()
    this._synced = false
    this.setStatus("connecting")

    const WS = this.connectOptions.WebSocketClass ?? WebSocket
    const socket = new WS(this.wsUrl)
    socket.binaryType = "arraybuffer"
    this.socket = socket
    this.attachAwarenessWire()
    crdtWsAudit("connecting", { url: redactWsUrl(this.wsUrl), session_id: this.sessionId })

    socket.onopen = () => {
      if (this.socket !== socket || this.disposed) return

      this.reconnectAttempt = 0
      this.setStatus("connected")
      crdtWsAudit("connected", { url: redactWsUrl(this.wsUrl), session_id: this.sessionId })
      crdtWsLog("log", "open", redactWsUrl(this.wsUrl))

      const doc = this.doc
      if (!doc) return

      try {
        const framed = encodeSyncStep1FromDoc(doc)
        socket.send(new Uint8Array(framed))
        console.log("[CRDT:Doc] SEND sync_step1 on open", {
          framedBytes: framed.byteLength,
          sessionId: this.sessionId,
        })
        const stateVector = Y.encodeStateVector(doc)
        if (isTrivialYjsUpdate(stateVector)) {
          crdtWsLog("log", "sync_step1 trivial on open — marking synced")
          this.markSynced()
        }
      } catch (error) {
        console.error("[CRDT:Doc] sync_step1 failed", error)
        crdtWsLog("error", "sync_step1 failed", error)
      }

      // If a sync_update was dropped while the socket was down (needsResync),
      // push full client state so the server converges — the server's own
      // sync_step1 already pulls our diff, but this covers the case where a
      // send "succeeded" and the server-side apply failed.
      const shouldSendFullState =
        (this.connectOptions.sendFullStateOnOpen ?? false) || this.needsResync
      if (shouldSendFullState && this.doc) {
        try {
          const fullUpdate = Y.encodeStateAsUpdate(this.doc)
          if (this.sendDocumentUpdate(fullUpdate)) {
            crdtWsLog("log", "full state resync sent", { bytes: fullUpdate.byteLength })
          }
        } catch (error) {
          this.needsResync = true
          crdtWsLog("error", "full state resync failed", error)
        }
      }

      this.broadcastLocalAwareness()
    }

    socket.onmessage = (event: MessageEvent<ArrayBuffer | Blob | string>) => {
      if (this.socket !== socket || this.disposed) return
      if (this.connectOptions.isStale?.()) return

      const handlePayload = (payload: Uint8Array) => {
        const doc = this.doc
        if (!doc || payload.byteLength === 0) return

        const applyFramedSync = (frame: Uint8Array, label: string): boolean => {
          try {
            const contentMap = doc.getMap<Y.Text>("content")
            const before: Record<string, number> = {}
            contentMap.forEach((yt, fileID) => {
              before[fileID] = yt.length
            })

            const { syncType, reply } = readSyncMessageFromFrame(frame, doc, {
              kind: "remote",
            } satisfies RemoteCrdtOrigin)

            const after: Record<string, number> = {}
            contentMap.forEach((yt, fileID) => {
              after[fileID] = yt.length
            })

            console.log("[CRDT:Doc] RECV/APPLY sync", {
              label,
              subType: syncSubtypeLabel(syncType),
              frameBytes: frame.byteLength,
              frameFirst10: bytesPreview(payload, 10),
              fileLengthsBefore: before,
              fileLengthsAfter: after,
              replyBytes: reply?.byteLength ?? 0,
              sessionId: this.sessionId,
            })

            if (reply && socket.readyState === WebSocket.OPEN) {
              socket.send(new Uint8Array(reply))
              console.log("[CRDT:Doc] SEND sync reply", {
                bytes: reply.byteLength,
                first10: bytesPreview(reply, 10),
              })
            }

            this.lastDocSyncRecvAt = Date.now()
            compilePdfTsLog("crdt:recv", "CRDT sync frame applied (remote → local)", {
              sessionId: this.sessionId,
              label,
              subType: syncSubtypeLabel(syncType),
              frameBytes: frame.byteLength,
              lastDocSyncSendAt: this.lastDocSyncSendAt || null,
              msSinceLastSend: this.lastDocSyncSendAt
                ? this.lastDocSyncRecvAt - this.lastDocSyncSendAt
                : null,
              fileLengthsAfter: after,
            })
            this.markSynced()
            return true
          } catch (error) {
            console.error("[CRDT:Doc] RECV sync apply failed", {
              label,
              error,
              frameBytes: payload.byteLength,
              first10: bytesPreview(payload, 10),
            })
            return false
          }
        }

        const applyRawYjsUpdate = (update: Uint8Array, label: string, opts?: { quiet?: boolean }): boolean => {
          try {
            const contentMap = doc.getMap<Y.Text>("content")
            const before: Record<string, number> = {}
            const beforePreview: Record<string, string> = {}
            contentMap.forEach((yt, fileID) => {
              before[fileID] = yt.length
              const s = yt.toString()
              beforePreview[fileID] = s.length > 40 ? `${s.slice(0, 20)}…${s.slice(-20)}` : s
            })

            Y.applyUpdate(doc, update, { kind: "remote" } satisfies RemoteCrdtOrigin)

            const after: Record<string, number> = {}
            const afterPreview: Record<string, string> = {}
            let anyContentChanged = false
            contentMap.forEach((yt, fileID) => {
              after[fileID] = yt.length
              const s = yt.toString()
              afterPreview[fileID] = s.length > 40 ? `${s.slice(0, 20)}…${s.slice(-20)}` : s
              if (beforePreview[fileID] !== afterPreview[fileID] || before[fileID] !== after[fileID]) {
                anyContentChanged = true
              }
            })

            console.log("[CRDT:Doc] RECV/APPLY relayed yjs update", {
              label,
              updateBytes: update.byteLength,
              frameBytes: payload.byteLength,
              frameFirst10: bytesPreview(payload, 10),
              fileLengthsBefore: before,
              fileLengthsAfter: after,
              contentChanged: anyContentChanged,
              beforePreview,
              afterPreview,
              sessionId: this.sessionId,
            })
            if (!anyContentChanged && update.byteLength > 8) {
              console.warn(
                "[CRDT:Doc] apply produced no contentMap content change — peer may be editing an orphan Y.Text or this is an echo/no-op",
                { updateBytes: update.byteLength, sessionId: this.sessionId },
              )
            }

            this.lastDocSyncRecvAt = Date.now()
            compilePdfTsLog("crdt:recv", "CRDT relayed yjs update applied (remote → local)", {
              sessionId: this.sessionId,
              label,
              updateBytes: update.byteLength,
              lastDocSyncSendAt: this.lastDocSyncSendAt || null,
              msSinceLastSend: this.lastDocSyncSendAt
                ? this.lastDocSyncRecvAt - this.lastDocSyncSendAt
                : null,
              fileLengthsAfter: after,
              contentChanged: anyContentChanged,
            })
            this.markSynced()
            return true
          } catch (error) {
            if (!opts?.quiet) {
              console.error("[CRDT:Doc] RECV relayed yjs apply failed", {
                label,
                error,
                first10: bytesPreview(payload, 10),
              })
            }
            return false
          }
        }

        // Compiler sync_update is [0x00][0x02] + raw Yjs bytes (no length prefix).
        // Older frames are [0x00][0x02][varuint len][update] and still go through
        // the y-protocols reader below.
        if (
          payload.byteLength > 2 &&
          payload[0] === Y_MESSAGE_SYNC &&
          payload[1] === Y_SYNC_UPDATE &&
          !isExactLengthPrefixedBlob(payload.subarray(2))
        ) {
          applyRawYjsUpdate(payload.subarray(2), "sync_update")
          return
        }

        // Standard y-protocols / y-websocket sync frame.
        if (payload[0] === Y_MESSAGE_SYNC) {
          applyFramedSync(payload, "sync")
          return
        }

        // Backend variant: document sync tagged as type 4.
        if (payload[0] === Y_MESSAGE_SYNC_ALT) {
          const asSync = new Uint8Array(payload)
          asSync[0] = Y_MESSAGE_SYNC
          if (applyFramedSync(asSync, "sync_alt(4)")) return
        }

        // Awareness: [0x01][raw payload] (backend guide).
        // Some backends also send doc sync_step2 as a bare `[1][…]` (missing outer sync
        // type 0), which collides with the awareness type byte — recover as document sync.
        if (payload[0] === Y_MESSAGE_AWARENESS) {
          const data = payload.slice(1)
          if (data.byteLength === 0) return

          let awarenessApplied = false
          if (this.awarenessWireEnabled) {
            try {
              applyAwarenessUpdate(this.awareness, data, "remote")
              this.awareness.getStates().forEach((state, clientId) => {
                if (clientId === this.doc?.clientID) return
                if (state && typeof state === "object") {
                  rehydrateAwarenessRelativePositions(state as Record<string, unknown>)
                }
              })
              awarenessApplied = true
              console.log("[CRDT:Awareness] remote update applied", {
                bytes: data.byteLength,
                peerCount: this.awareness.getStates().size - 1,
              })
            } catch {
              awarenessApplied = false
            }
          }

          if (awarenessApplied) return

          // Server sync_step2 is an unframed Yjs update. Those bytes often start
          // with 0x01, which is also the awareness message type.
          if (applyRawYjsUpdate(payload, "raw-sync-step2", { quiet: true })) return

          // Doc-recovery only for frames that look like sync payloads.
          // Do NOT run the aggressive "firstByte > 4 ⇒ raw yjs" heuristic here —
          // those attempts just throw TextDecoder noise on this backend's ~1016B junk frames.
          const remapped = remapBareSyncStepToFramed(payload)
          if (remapped && applyFramedSync(remapped, "awareness→sync_step2")) {
            crdtWsLog("log", "mis-tagged awareness frame applied as sync_step2")
            return
          }

          if (isExactLengthPrefixedBlob(data)) {
            const relayed = extractRelayedYjsUpdate(data)
            if (relayed && applyRawYjsUpdate(relayed, "awareness→relayed", { quiet: true })) {
              crdtWsLog("log", "mis-tagged awareness frame applied as relayed yjs update")
              return
            }
          }

          // Backend occasionally echoes a fixed ~1016B binary blob tagged 0x01 that is
          // neither valid awareness nor a valid Yjs update. Safe to ignore quietly.
          crdtWsLog("log", "skipping non-awareness 0x01 frame", {
            bytes: data.byteLength,
            first10: bytesPreview(data, 10),
          })
          return
        }

        if (payload[0] === Y_MESSAGE_AUTH) {
          crdtWsLog("log", "recv auth (ignored)")
          return
        }

        if (payload[0] === Y_MESSAGE_QUERY_AWARENESS) {
          if (payload.byteLength > 32) {
            const asSync = new Uint8Array(payload)
            asSync[0] = Y_MESSAGE_SYNC
            if (applyFramedSync(asSync, "query_awareness→sync")) return
          }
          if (!this.awarenessWireEnabled) return
          try {
            const encoded = encodeAwarenessUpdate(
              this.awareness,
              Array.from(this.awareness.getStates().keys()),
            )
            socket.send(new Uint8Array(encodeAwarenessMessage(encoded)))
            console.log("[CRDT:Doc] RECV query_awareness → sent awareness snapshot")
          } catch (error) {
            console.error("[CRDT:Doc] query_awareness reply failed", error)
          }
          return
        }

        // Backend relays live edits as bare varUint8Array(update) (no [0][2] wrapper).
        // Chrome logs showed mainType 26 / 2023 — that was the length prefix, not a msg type.
        const relayed = extractRelayedYjsUpdate(payload)
        if (relayed && applyRawYjsUpdate(relayed, "bare-length-prefixed")) {
          return
        }

        // Last resort: remap unknown type byte → sync and try y-protocols reader.
        if (payload.byteLength > 8) {
          const asSync = new Uint8Array(payload)
          asSync[0] = Y_MESSAGE_SYNC
          if (applyFramedSync(asSync, `remap(${payload[0]})`)) return
        }

        console.warn("[CRDT:Doc] RECV unknown message type", {
          mainType: payload[0],
          bytes: payload.byteLength,
          first10: bytesPreview(payload, 10),
        })
      }

      if (typeof event.data === "string") {
        crdtWsLog("error", "unexpected text frame")
        return
      }

      if (event.data instanceof Blob) {
        void event.data
          .arrayBuffer()
          .then((buffer) => handlePayload(new Uint8Array(buffer)))
          .catch((error) => crdtWsLog("error", "blob read failed", error))
        return
      }

      handlePayload(new Uint8Array(event.data))
    }

    socket.onclose = (event: CloseEvent) => {
      if (this.socket !== socket) return

      this.socket = null
      this._synced = false
      this.needsResync = true
      this.handleAwarenessDisconnectCleanup()
      this.setStatus("disconnected")
      crdtWsLog("warn", "close", { code: event.code, reason: event.reason })

      if (!this.disposed && !this.connectOptions.isStale?.()) {
        this.scheduleReconnect()
      }
    }

    socket.onerror = (event: Event) => {
      if (this.socket !== socket) return
      crdtWsLog("error", "socket error", event)
      this.handlers.onError?.(event)
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      crdtWsLog("warn", "reconnect abandoned", { attempts: this.reconnectAttempt })
      return
    }

    this.reconnectAttempt += 1
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempt - 1), MAX_BACKOFF_MS)
    crdtWsLog("log", `reconnect in ${delay}ms`, { attempt: this.reconnectAttempt })

    this.clearReconnectTimer()
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      if (this.disposed || this.connectOptions.isStale?.()) return
      void this.openSocketWithResolvedUrl()
    }, delay)
  }

  private closeSocket(): void {
    if (this.socket) {
      try {
        this.socket.onopen = null
        this.socket.onmessage = null
        this.socket.onclose = null
        this.socket.onerror = null
        this.socket.close()
      } catch {
        /* ignore */
      }
      this.socket = null
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

}
